/** Data hooks for judgments. All fetching goes through React Query. */
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { api, type SearchQuery } from '@/lib/apiClient'
import { queryKeys } from '@/lib/queryKeys'
import type { JudgmentFilters } from '@/types/api'

export function useJudgmentSearch(query: SearchQuery) {
  return useQuery({
    queryKey: queryKeys.judgments.search(query),
    queryFn: () => api.searchJudgments(query),
    // Hold the previous page on screen while the next one loads, so paging and
    // sorting do not blank the table.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
}

export function useJudgmentDetail(id: number | null) {
  return useQuery({
    queryKey: queryKeys.judgments.detail(id ?? 0),
    queryFn: () => api.getJudgment(id as number),
    enabled: id !== null,
    staleTime: 5 * 60_000,
  })
}

export function useFacets(filters: JudgmentFilters) {
  return useQuery({
    queryKey: queryKeys.judgments.facets(filters),
    queryFn: () => api.getFacets(filters),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}

export function useStats() {
  return useQuery({
    queryKey: queryKeys.judgments.stats(),
    queryFn: () => api.getStats(),
    staleTime: 60_000,
  })
}

export function useNameSuggestions(prefix: string) {
  return useQuery({
    queryKey: queryKeys.judgments.suggest(prefix),
    queryFn: () => api.suggestNames(prefix),
    enabled: prefix.trim().length >= 2,
    staleTime: 5 * 60_000,
  })
}
